import React, { Fragment, useEffect, useState } from 'react';
import { Datagrid, RecordContextProvider, SimpleForm, useDataProvider, useListContext, useNotify, useRecordContext, useRefresh, useResourceContext, useTranslate } from "react-admin";
import { useForm, useFormState } from 'react-final-form';
import { Button, Dialog, DialogContent, DialogTitle, Grid, Typography, useMediaQuery } from "@material-ui/core";
import { PlaylistAdd } from '@material-ui/icons';

import { filterParticipants } from '@hisports/scoresheet/src/util';

import { DialogFormToolbar } from '../../common/dialogs/DialogForm';
import SelectedItems from '../../common/SelectedItems';
import ListCard from '../../common/cards/ListCard';

import { ListLoader, ListToolbar, SearchInput } from '../officials/SelectOfficialsDialog';
import { ParticipantField } from '../participants/ParticipantField';

const DEFAULT_FORM_VALUES = {
  participant: null,
};

const inputProps = {
  variant: 'outlined',
  margin: 'none',
  fullWidth: true,
  helperText: false,
}

const getFilters = (values) => {
  if (!values) return null;

  const { participant } = values;

  return {
    ...(participant && filterParticipants(participant))
  }
}

const SelectedMembers = () => {
  const { values: { participantIds = [] } } = useFormState();
  const { onToggleItem, onUnselectItems } = useListContext();
  const translate = useTranslate();

  const items = participantIds?.map(participantId => ({ id: participantId, participantId })) || [];

  const actions = [
    !!participantIds?.length && <Button color="primary" size="small" onClick={onUnselectItems}>{translate("resources.members.actions.remove_all")}</Button>
  ].filter(Boolean);

  return <SelectedItems
    title="resources.members.labels.selected_members"
    noResultsText="resources.members.messages.no_members_selected"
    items={items}
    actions={actions}
    onRemove={(item) => onToggleItem(item.participantId)}
  >
    <ParticipantField source="participantId" label="resources.members.fields.participantId" includeId="inline" link={false} />
  </SelectedItems>
};

const ParticipantGrid = (props) => {
  const { selectedIds } = useListContext();
  const { change } = useForm();

  useEffect(() => {
    change('participantIds', selectedIds);
  }, [selectedIds, change]);

  return <Datagrid {...props} size="medium" rowClick="toggleSelection" hasBulkActions>
    <ParticipantField source="id" label="resources.members.fields.participantId" includeId="inline" />
  </Datagrid>
}

const MembersList = () => {
  const { change, batch } = useForm();
  const { values, initialValues } = useFormState();
  const [filters, setFilters] = useState(getFilters(initialValues));

  const handleSearch = () => {
    setFilters(getFilters(values));
  };

  const handleReset = () => {
    batch(() => Object.entries(initialValues).forEach(([key, value]) => change(key, value)));
    setFilters(initialValues);
  }

  return <ListCard
    component={Fragment}
    reference="participants"
    target="participantId"
    filter={filters}
    sort={{ field: 'lastName', order: 'ASC' }}
    perPage={10}
    rowsPerPageOptions={[10, 25, 50]}
    additionalContent={<div style={{ paddingBottom: 16 }}>
      <ListToolbar handleSearch={handleSearch} handleReset={handleReset} />
      <SelectedMembers />
      <ListLoader />
    </div>}
  >
    <ParticipantGrid />
  </ListCard>
}

const SelectMembersForm = ({ onClose, initialValues, ...props }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSubmit = ({ participantIds, teamId }) => {
    if (!participantIds?.length) return;

    const members = participantIds.map(participantId => ({
      teamId,
      participantId,
      number: 0,
      positions: ['F'],
    }));

    return dataProvider.createMany(resource, { data: members })
      .then(data => data.data)
      .then(data => {
        onClose();
        refresh();
        notify(translate('resources.members.notifications.members_added', data.length || 0));
      })
      .catch(() => {
        onClose();
        notify(translate('resources.members.notifications.add_members_error'), 'warning');
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return <SimpleForm save={onSubmit} component={Fragment} initialValues={{ ...DEFAULT_FORM_VALUES, ...initialValues }} onKeyDown={handleKeyDown} {...props}>
    <Typography variant="subtitle1" color="textSecondary" component="h3" gutterBottom style={{ fontWeight: 500 }}>
      {translate('ra.message.filters')}
    </Typography>
    <Grid container spacing={2} alignItems="center" style={{ paddingTop: 8 }}>
      <Grid item xs={12}>
        <SearchInput source="participant" label={translate("resources.members.name", 1)} {...inputProps} helperText="resources.members.helpers.name_or_id" />
      </Grid>
      <Grid item xs={12}>
        <MembersList />
      </Grid>
    </Grid>
  </SimpleForm>
}

const SelectMembersDialog = ({ title, isOpen, onSubmit, onClose, ...props }) => {
  const fullScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return <Dialog open={isOpen} maxWidth="md" fullWidth fullScreen={fullScreen}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent style={{ paddingTop: 0 }}>
      <RecordContextProvider value={{}}>
        <SelectMembersForm
          onClose={onClose}
          toolbar={<DialogFormToolbar submitLabel="ra.action.add" cancelLabel="ra.action.cancel" onCancel={onClose} />}
          {...props}
        />
      </RecordContextProvider>
    </DialogContent>
  </Dialog>
}

export const SelectMembersButton = ({ size = "small", variant, ...props }) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const [open, setOpen] = useState(false);

  return <>
    <Button color="primary" size={size} variant={variant} startIcon={<PlaylistAdd fontSize="small" />} onClick={() => setOpen(true)}>
      {translate('resources.members.actions.add_members')}
    </Button>
    <SelectMembersDialog
      title={translate('resources.members.labels.add_members', { name: record?.name })}
      isOpen={open}
      onClose={() => setOpen(false)}
      {...props}
    />
  </>
}
